// Small per-viewer choices about how things look, remembered on this device only and never sent anywhere:
// which link previews and attachments I collapsed, and which private pictures I chose to look at.
// Storage can be missing or throw (private windows, blocked site data), so every read and write is guarded and the
// app works the same without it; the choice then just lasts until reload.
import { useSyncExternalStore } from "react";

/** A remembered set of strings in localStorage, newest last, capped so it can't grow forever. */
function storedSet(key: string, cap: number) {
  let items: string[] | null = null;
  const listeners = new Set<() => void>();
  const load = () => {
    if (items) return items;
    try {
      const raw = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
      items = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : [];
    } catch {
      items = [];
    }
    return items;
  };
  const save = () => {
    try {
      localStorage.setItem(key, JSON.stringify(items));
    } catch {
      // fine: remembered for this visit only
    }
  };
  // Another tab changed it: pick that up so both agree.
  addEventListener("storage", (e) => {
    if (e.key !== key) return;
    items = null;
    listeners.forEach((l) => l());
  });
  return {
    has: (id: string) => load().includes(id),
    set(id: string, on: boolean) {
      const list = load().filter((x) => x !== id);
      if (on) list.push(id);
      items = list.slice(-cap);
      save();
      listeners.forEach((l) => l());
    },
    subscribe(l: () => void) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

/** Link previews and attachments I collapsed, by "<messageId>|<url or attachment ref>". */
export const collapsed = storedSet("bloom:collapsed", 2000);
/** Private pictures I chose to see, by attachment ref (an R2 key or a GIF URL). */
export const revealed = storedSet("bloom:revealed", 2000);

type Store = typeof collapsed;

/** Whether `id` is in the set, kept current as it changes anywhere on the page (or in another tab). */
export function useInSet(store: Store, id: string): boolean {
  return useSyncExternalStore(store.subscribe, () => store.has(id));
}
